// Loans catalogue (spec §4) and static mock data: the 13 products in
// catalogue order, the securities holdings (§9.2), PayLater partners (§13.3)
// and the soft-check multipliers (§6.2).
import type { Holding, LoanProduct, LoanProductId } from '@/types/loan.types';
import { REPO_RATE_PCT } from '@/utils/loans';

const f = (spreadPct: number): number => REPO_RATE_PCT + spreadPct;

export const LOAN_PRODUCTS: LoanProduct[] = [
  {
    id: 'PERSONAL', name: 'Personal / Short-Term Loan', tagline: 'Instant money up to your pre-approved limit',
    minRupees: 1_000, maxRupees: 5_00_000, spreadPct: undefined, fee: { kind: 'PCT', pct: 6 },
    missedEmiChargeRupees: 500, documents: [], isRevolving: false, family: 'INSTANT',
  },
  {
    id: 'HOME', name: 'Home Loan', tagline: 'Buy, build or complete a home',
    minRupees: 5_00_000, maxRupees: 5_00_00_000, tenureMinMonths: 60, tenureMaxMonths: 360, spreadPct: 2.85,
    fee: { kind: 'PCT', pct: 0.5, maxRupees: 25_000 }, missedEmiChargeRupees: 750,
    documents: ['3 months salary slips', '6-month bank statement', 'ITR for 2 years', 'Property papers'],
    isRevolving: false, family: 'TERM',
  },
  {
    id: 'VEHICLE', name: 'Vehicle Loan / Asset Finance', tagline: 'New or used vehicles through our dealers',
    minRupees: 50_000, maxRupees: 50_00_000, tenureMinMonths: 12, tenureMaxMonths: 96, spreadPct: 3.25,
    fee: { kind: 'FLAT', flatRupees: 3_500 }, missedEmiChargeRupees: 500,
    documents: ['Salary slips', 'Bank statement', 'Vehicle quotation'],
    isRevolving: false, family: 'TERM',
  },
  {
    id: 'LAP', name: 'Loan Against Property', tagline: 'Borrow against a home or shop you own',
    minRupees: 10_00_000, maxRupees: 5_00_00_000, tenureMinMonths: 36, tenureMaxMonths: 240, spreadPct: 3.75,
    fee: { kind: 'PCT', pct: 1, maxRupees: 50_000 }, missedEmiChargeRupees: 1_000,
    documents: ['Bank statement', 'ITR', 'Property papers'],
    isRevolving: false, family: 'TERM',
  },
  {
    id: 'EDUCATION', name: 'Education Loan', tagline: 'Study now, start repaying after the course',
    minRupees: 1_00_000, maxRupees: 75_00_000, tenureMinMonths: 60, tenureMaxMonths: 180, spreadPct: 3.5,
    fee: { kind: 'NONE' }, missedEmiChargeRupees: 300,
    documents: ['Admission letter', 'Fee structure', 'Bank statement'],
    isRevolving: false, family: 'TERM',
  },
  {
    id: 'GOLD', name: 'Gold Loan', tagline: 'Pledge gold, money in minutes',
    minRupees: 10_000, maxRupees: 50_00_000, tenureMinMonths: 3, tenureMaxMonths: 36, spreadPct: 3.5,
    fee: { kind: 'PCT', pct: 0.5, maxRupees: 5_000 }, missedEmiChargeRupees: 250,
    documents: [], isRevolving: false, family: 'GOLD',
  },
  {
    id: 'BUSINESS', name: 'Business Loan', tagline: 'A term loan for your business',
    minRupees: 5_00_000, maxRupees: 5_00_00_000, tenureMinMonths: 36, tenureMaxMonths: 120, spreadPct: 5.5,
    fee: { kind: 'PCT', pct: 2, maxRupees: 50_000 }, missedEmiChargeRupees: 1_000,
    documents: ['Bank statement', 'ITR', 'GST returns', 'Udyam certificate'],
    isRevolving: false, family: 'BUSINESS',
  },
  {
    id: 'WORKING_CAPITAL', name: 'Working Capital Limit', tagline: 'A revolving limit for day-to-day business',
    minRupees: 5_00_000, maxRupees: 5_00_00_000, spreadPct: 5.5,
    fee: { kind: 'PCT', pct: 2, maxRupees: 50_000 }, missedEmiChargeRupees: 1_000,
    documents: ['Bank statement', 'ITR', 'GST returns', 'Udyam certificate'],
    isRevolving: true, family: 'BUSINESS',
  },
  {
    id: 'OVERDRAFT', name: 'Digital Overdraft', tagline: 'A limit on your account — use it when you need it',
    minRupees: 25_000, maxRupees: 10_00_000, spreadPct: 5.5,
    fee: { kind: 'FLAT', flatRupees: 999 }, missedEmiChargeRupees: 250,
    documents: [], isRevolving: true, family: 'TERM',
  },
  {
    id: 'LAMF', name: 'Loan Against Mutual Funds', tagline: 'Pledge your funds, keep them growing',
    minRupees: 50_000, maxRupees: 1_00_00_000, spreadPct: 3.5,
    fee: { kind: 'FLAT', flatRupees: 999 }, missedEmiChargeRupees: 500,
    documents: ['Holding statement'], isRevolving: true, family: 'SECURITIES',
  },
  {
    id: 'LAS', name: 'Loan Against Shares', tagline: 'Pledge shares, ETFs or REITs without selling',
    minRupees: 50_000, maxRupees: 1_00_00_000, spreadPct: 3.5,
    fee: { kind: 'FLAT', flatRupees: 999 }, missedEmiChargeRupees: 500,
    documents: ['Holding statement'], isRevolving: true, family: 'SECURITIES',
  },
  {
    id: 'PAYLATER', name: 'PayLater', tagline: 'Shop at partner stores, 30 days interest-free',
    minRupees: 5_000, maxRupees: 2_00_000, spreadPct: undefined,
    fee: { kind: 'NONE' }, missedEmiChargeRupees: 250,
    documents: [], isRevolving: true, family: 'BNPL',
  },
  {
    id: 'SALARY_ADVANCE', name: 'Salary Advance', tagline: 'Up to half your salary, repaid on payday',
    minRupees: 1_000, maxRupees: 1_00_000, spreadPct: undefined,
    fee: { kind: 'FLAT', flatRupees: 199 }, missedEmiChargeRupees: 0,
    documents: [], isRevolving: false, family: 'SALARY',
  },
];

export const productById = (id: LoanProductId): LoanProduct => {
  const p = LOAN_PRODUCTS.find((x) => x.id === id);
  if (!p) throw new Error(`unknown loan product ${id}`);
  return p;
};

/** Floating rate today (repo 6.5 + spread). PayLater/instant price elsewhere. */
export const floatingRatePct = (product: LoanProduct): number =>
  product.spreadPct === undefined ? NaN : f(product.spreadPct);

export const HOLDINGS: Holding[] = [
  { id: 'H1', name: 'NovaBank Bluechip Equity Fund', type: 'EQUITY_FUND', valueRupees: 3_20_000, ltvPct: 75 },
  { id: 'H2', name: 'Flexi Cap Growth Fund', type: 'EQUITY_FUND', valueRupees: 1_85_000, ltvPct: 75 },
  { id: 'H3', name: 'Short Duration Debt Fund', type: 'DEBT_FUND', valueRupees: 2_40_000, ltvPct: 85 },
  { id: 'H4', name: 'Liquid Fund', type: 'DEBT_FUND', valueRupees: 1_10_000, ltvPct: 85 },
  { id: 'H5', name: 'Bharat Industries Ltd', type: 'SHARE', valueRupees: 3_40_000, ltvPct: 60 },
  { id: 'H6', name: 'Deccan Bank Ltd', type: 'SHARE', valueRupees: 2_50_000, ltvPct: 60 },
  { id: 'H7', name: 'Sahyadri Infotech Ltd', type: 'SHARE', valueRupees: 1_60_000, ltvPct: 60 },
  { id: 'H8', name: 'Nifty 50 ETF', type: 'ETF', valueRupees: 90_000, ltvPct: 75 },
  { id: 'H9', name: 'Embassy Office Parks REIT', type: 'REIT', valueRupees: 60_000, ltvPct: 75 },
];

export const BNPL_PARTNERS = [
  { code: 'PRT001', name: 'Croma' },
  { code: 'PRT002', name: 'Reliance Digital' },
  { code: 'PRT003', name: 'Decathlon' },
  { code: 'PRT004', name: 'Pepperfry' },
];

/** Soft-check offer multipliers (§6.2), capped at the product maximum. */
export const OFFER_MULTIPLIERS: Partial<Record<LoanProductId, number>> = {
  PERSONAL: 20, HOME: 60, VEHICLE: 36, LAP: 60, EDUCATION: 48,
  BUSINESS: 40, WORKING_CAPITAL: 20, OVERDRAFT: 2, PAYLATER: 1,
};

export const APPROVED_PROJECTS = [
  'Prestige Lakeside Habitat, Bengaluru',
  'Lodha Palava City, Mumbai',
  'DLF The Camellias, Gurugram',
  'Brigade Utopia, Bengaluru',
];

export const VEHICLE_DEALERS = [
  'Sunrise Motors, Pune',
  'City Honda, Bengaluru',
  'Green Wheels EV, Hyderabad',
  'Metro Hero, Delhi',
];

export const PROPERTY_TYPES_HOME = ['Ready to move', 'Under construction', 'Plot plus construction', 'Resale'];
export const PROPERTY_TYPES_LAP = ['Residential', 'Commercial', 'Industrial', 'Plot'];
export const VEHICLE_TYPES = ['Car', 'Two-wheeler', 'Commercial vehicle', 'Electric vehicle'];

export const LOAN_STATE_LABELS: Record<string, string> = {
  enquiry: 'Enquiry',
  eligibilityChecked: 'Eligibility checked',
  applicationSubmitted: 'Application submitted',
  kfsGenerated: 'Key Fact Statement ready',
  kfsAcknowledged: 'KFS accepted',
  documentsSubmitted: 'Documents submitted',
  underReview: 'Under review',
  approved: 'Approved',
  rejected: 'Not approved',
  cancelled: 'Cancelled',
  disbursed: 'Disbursed',
  coolingOff: 'Cooling-off period',
  coolingOffExited: 'Returned during cooling-off',
  active: 'Active',
  moratorium: 'Moratorium',
  unutilised: 'Sanctioned, not used',
  overdue: 'EMI Due',
  closed: 'Closed',
  foreclosed: 'Foreclosed',
  defaulted: 'Defaulted',
};

export const LOAN_FAQS = [
  { q: 'How is my rate decided?', a: 'Every loan to an individual is floating-rate: the RBI repo rate plus the product spread. Your CIBIL score only decides eligibility, not the rate. PayLater is fixed and priced inside its band by score.' },
  { q: 'What if my CIBIL score is low?', a: 'A score below 700, or no credit history, means the bank cannot offer this loan right now. Build your score and try again.' },
  { q: 'When do I get the money?', a: 'Once the bank approves your application, accept and disburse from the Loans hub. The money lands in your NovaBank account right away (education fees go to the institute).' },
  { q: 'How are EMIs collected?', a: 'Automatically on your chosen date — the 5th, 10th or 15th — by auto-debit from your NovaBank account, e-NACH mandate or UPI AutoPay.' },
  { q: 'What is the cooling-off period?', a: 'You can return the loan within 1 day (tenure under 7 days) or 3 days by repaying the principal plus interest for the days held. The processing fee is not refunded.' },
];
